"use client";
import { useEffect, useMemo, useRef, useState } from "react";

import { BackgroundTrackingWarmer, type WarmTarget } from "@perceptron/core";
import { MediaKind } from "@perceptron/utils/types";

import { DEMO_NANO_MODEL_URLS } from "@demo/constants/tracking";
import { mergeAnnotationSets } from "@demo/hooks/annotations/annotationIndex";
import { annotationsForAsset } from "@demo/hooks/annotations/assetPartition";
import { MediaFactory } from "@demo/libs/media-factory";
import { getTrackingRecoveryProvider } from "@demo/libs/tracking-recovery";
import { useDisplayedMediaIndex, useMediaCarouselStore } from "@demo/store/media-carousel";
import { useModelStore } from "@demo/store/model";

import { annotationsToTimelineItems } from "./timelineItems";

/**
 * Tracks the run's other videos while one is on screen, so switching to a
 * second video shows finished work rather than starting from nothing.
 *
 * The asset being viewed is deliberately excluded: its own player already
 * tracks it, and duplicating that would compete for the same workers. Warming
 * also yields entirely while that player has intervals in flight — background
 * work that slows down the video someone is watching is worse than none.
 */
export function useBackgroundWarming(): { onActivePlayerBusyChange: (busy: boolean) => void } {
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const displayedIndex = useDisplayedMediaIndex();
  const annotations = useModelStore((state) => state.annotations);
  const reasoningAnnotations = useModelStore((state) => state.reasoningAnnotations);
  const outputRunContext = useModelStore((state) => state.outputRunContext);
  const [activeBusy, setActiveBusy] = useState(false);

  const warmer = useMemo(() => new BackgroundTrackingWarmer(), []);
  useEffect(() => () => warmer.dispose(), [warmer]);

  const targets = useMemo<WarmTarget[]>(() => {
    if (media.length < 2) return [];
    const all = mergeAnnotationSets(reasoningAnnotations, annotations);

    const candidates = media.flatMap((item, assetIdx) => {
      if (assetIdx === displayedIndex) return [];
      if (!item.url || MediaFactory.detectMediaKind(item) !== MediaKind.Video) return [];
      const items = annotationsToTimelineItems(annotationsForAsset(all, assetIdx));
      if (!items.length) return [];
      const target = {
        src: item.url,
        nanoModelUrls: DEMO_NANO_MODEL_URLS,
        items,
        // Matches what this asset's player will be given, so the results it
        // writes are the ones that player later looks up.
        recoveryProvider: getTrackingRecoveryProvider(outputRunContext, item.key),
      } satisfies WarmTarget;
      return [{ assetIdx, target }];
    });

    // Nearest first, which is the order the warmer takes them in. Someone
    // moving off this asset reaches a neighbour before anything further out,
    // so the neighbours are the ones worth having finished.
    candidates.sort((a, b) => Math.abs(a.assetIdx - displayedIndex) - Math.abs(b.assetIdx - displayedIndex));
    return candidates.map((entry) => entry.target);
  }, [media, displayedIndex, annotations, reasoningAnnotations, outputRunContext]);

  useEffect(() => warmer.update(targets), [warmer, targets]);
  useEffect(() => warmer.setPaused(activeBusy), [warmer, activeBusy]);

  const report = useRef(setActiveBusy);
  return { onActivePlayerBusyChange: report.current };
}
